import React, { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import moment from "moment";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { FaArrowLeftLong } from "react-icons/fa6";
import { FiCopy, FiUser, FiTruck, FiCreditCard, FiClipboard, FiBox, FiAlertCircle, FiPackage } from "react-icons/fi";
import toast from "react-hot-toast";
import Loder from "../components/Loder"; // Ensure this path is correct

const fetchOrder = async (id) => {
  const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:8080/api/"; // Updated port
  const res = await axios.get(`${baseUrl}order/order-details/${id}`);
  return res.data.data; // Assuming API returns data under a 'data' field
};

const OrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const {
    data: order,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["orderDetails", id],
    queryFn: () => fetchOrder(id),
    retry: 1,
  });

  const handleCopy = (textToCopy, message) => {
    if (textToCopy) {
      navigator.clipboard.writeText(textToCopy)
        .then(() => toast.success(message || "Copied to clipboard!"))
        .catch(err => {
          console.error("Failed to copy:", err);
          toast.error("Failed to copy.");
        });
    }
  };

  const primaryAccentColor = "#e5272e";
  const gradientColor = "linear-gradient(to right, #e5272e, #f59332)";

  const getStatusBadgeStyle = (status) => {
    let backgroundColor = "#6c757d";
    let color = "#fff";

    if (!status) return { backgroundColor, color, text: "Unknown" };

    const lowerStatus = status.toLowerCase();

    if (lowerStatus === "delivered" || lowerStatus === "completed") {
      backgroundColor = "rgba(22, 163, 74, 0.1)";
      color = "#16a34a";
    } else if (lowerStatus === "processing" || lowerStatus === "shipped" || lowerStatus === "out for delivery") {
      backgroundColor = "rgba(245, 147, 50, 0.1)";
      color = "#f59332";
    } else if (lowerStatus === "pending" || lowerStatus === "on hold") {
      backgroundColor = "rgba(245, 180, 50, 0.1)";
      color = "#f5b432";
    } else if (lowerStatus === "cancelled" || lowerStatus === "canceled" || lowerStatus === "failed" || lowerStatus === "refunded") {
      backgroundColor = "rgba(239, 68, 68, 0.1)";
      color = "#ef4444";
    }
    return { backgroundColor, color, textTransform: 'capitalize', fontWeight: '600', padding: '0.3em 0.7em', borderRadius: '20px', fontSize: '0.8rem' };
  };

  if (isLoading)
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "70vh", width: "100%" }}
      >
        <Loder />
      </div>
    );

  if (isError || !order)
    return (
      <div className="container text-center py-5">
        <FiAlertCircle size={60} className="mb-3 text-danger" />
        <h3 className="text-danger mb-3">Error Loading Order</h3>
        <p className="text-muted">
          {error?.response?.data?.message || error?.message || "Could not find the requested order or an error occurred."}
        </p>
        <button
          onClick={() => navigate("/myaccount", { state: { toggle: 2 } })}
          className="btn mt-3"
          style={{ backgroundImage: gradientColor, color: 'white', padding: '10px 25px', fontWeight: '600' }}
        >
          <FaArrowLeftLong className="me-2" /> Back to My Orders
        </button>
      </div>
    );

  // Destructure with fallbacks for safety, matching the provided JSON structure
  const {
    orderId = "N/A",
    customer = {},
    shippingAddress = {},
    payment = {},
    status = "N/A",
    orderDate,
    deliveryPriority,
    products = [],
  } = order;

  return (
    <>
      <style>
        {`
        .order-details-page {
            background-color: #f9fafb;
        }
        .btn-back-order {
            color: ${primaryAccentColor};
            font-weight: 600;
            padding: 0.5rem 1rem;
            border-radius: 25px;
            transition: background-color 0.2s ease;
        }
        .btn-back-order:hover {
            background-color: rgba(229, 39, 46, 0.1);
        }
        .page-title-order {
            color: #111827;
            font-weight: 700;
            font-size: 2.25rem;
        }
        .info-card-order {
            background-color: #fff;
            border-radius: 12px;
            box-shadow: 0 6px 18px rgba(0,0,0,0.06);
            height: 100%;
            border: 1px solid #e5e7eb;
        }
        .info-card-order .card-header {
            background-image: linear-gradient(to right, #374151, #1f2937);
            color: white;
            font-weight: 600;
            border-top-left-radius: 12px;
            border-top-right-radius: 12px;
            padding: 0.75rem 1.25rem;
            display: flex;
            align-items: center;
        }
        .info-card-order .card-header svg {
            margin-right: 10px;
            font-size: 1.2em;
        }
        .info-card-order .card-body p {
            margin-bottom: 0.5rem;
            font-size: 0.95rem;
            color: #4b5563;
        }
        .info-card-order .card-body strong {
            color: #1f2937;
        }

        .order-summary-section {
            background-color: #fff;
            padding: 1.5rem;
            border-radius: 12px;
            box-shadow: 0 6px 18px rgba(0,0,0,0.06);
            border: 1px solid #e5e7eb;
        }
        .order-summary-section h5 {
            color: ${primaryAccentColor};
            font-weight: 600;
            margin-bottom: 1rem;
        }
        .order-summary-section p {
            font-size: 1rem;
            color: #374151;
        }
        .order-summary-section .copy-icon {
            cursor: pointer;
            color: ${primaryAccentColor};
            transition: color 0.2s ease;
        }
        .order-summary-section .copy-icon:hover {
            color: #f59332;
        }

        .products-table th {
            background-color: #f3f4f6;
            color: #374151;
            font-weight: 600;
            font-size: 0.9rem;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }
        .products-table td {
            vertical-align: middle;
            font-size: 0.95rem;
        }
        .products-table .product-image-order {
            height: 60px;
            width: 60px;
            object-fit: contain;
            border-radius: 6px;
            background-color: #fff;
            border: 1px solid #e5e7eb;
        }
        .products-table .attributes-list {
            font-size: 0.85rem;
            color: #6b7280;
            padding-left: 1rem;
        }
        .products-table .attributes-list li {
            margin-bottom: 0.15rem;
        }
    `}
      </style>
      <div className="container my-4 my-md-5 order-details-page">
        <button
          onClick={() => navigate("/myaccount", { state: { toggle: 2 } })}
          className="btn btn-back-order d-flex align-items-center gap-2 mb-4"
        >
          <FaArrowLeftLong />
          <span>Back to My Orders</span>
        </button>

        <h1 className="mb-4 page-title-order">Order Details</h1>

        <div className="row g-4 mb-4">
          <div className="col-lg-4 col-md-6">
            <div className="card info-card-order">
              <div className="card-header"><FiUser />Customer Information</div>
              <div className="card-body">
                <p><strong>Name:</strong> {customer.name || "N/A"}</p>
                <p><strong>Phone:</strong> {customer.phone || "N/A"}</p>
                <p><strong>Email:</strong> {customer.email || "N/A"}</p>
              </div>
            </div>
          </div>

          <div className="col-lg-4 col-md-6">
            <div className="card info-card-order">
              <div className="card-header"><FiTruck />Shipping Address</div>
              <div className="card-body">
                <p><strong>Country:</strong> {shippingAddress.country || "N/A"}</p>
                <p><strong>Address:</strong> {shippingAddress.address || "N/A"}</p>
              </div>
            </div>
          </div>

          <div className="col-lg-4 col-md-12">
            <div className="card info-card-order">
              <div className="card-header"><FiCreditCard />Payment Information</div>
              <div className="card-body">
                <p><strong>Method:</strong> <span className="text-uppercase">{payment.method || "N/A"}</span></p>
                {payment.paymentId && <p><strong>Payment ID:</strong> {payment.paymentId}</p>}
                <p><strong>Shipping:</strong> £{parseFloat(payment.shippingAmount || 0).toFixed(2)}</p>
                <p><strong>Total Paid:</strong> <strong style={{ color: primaryAccentColor, fontSize: '1.1em' }}>£{parseFloat(payment.total || 0).toFixed(2)}</strong></p>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-5 order-summary-section">
          <h4 className="fw-bold mb-3" style={{ color: primaryAccentColor }}>Order Summary</h4>
          <p className="mb-2 d-flex align-items-center">
            <strong>Order ID:</strong>
            <span className="text-dark fw-semibold ms-2 me-2">{orderId}</span>
            <FiCopy
              size={18}
              className="copy-icon"
              role="button"
              onClick={() => handleCopy(orderId, "Order ID copied!")}
              title="Copy Order ID"
            />
          </p>
          <p className="mb-2">
            <strong>Status:</strong>
            <span className="ms-2" style={getStatusBadgeStyle(status)}>{status}</span>
          </p>
          <p className="mb-2">
            <strong>Order Date:</strong> {moment(orderDate).format("MMMM Do YYYY, h:mm A")}
          </p>
          {deliveryPriority && <p className="mb-1"><strong>Delivery Priority:</strong> <span style={{textTransform: 'capitalize'}}>{deliveryPriority}</span></p>}
        </div>

        <div>
          <h4 className="fw-bold mb-3" style={{ color: primaryAccentColor }}><FiPackage className="me-2" />Products in this Order</h4>
          <div className="table-responsive shadow-sm bg-white rounded-3">
            <table className="table table-hover align-middle products-table mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: '80px' }}>Image</th>
                  <th>Name</th>
                  <th className="text-center">Price</th>
                  <th className="text-center">Quantity</th>
                  <th>Attributes</th>
                </tr>
              </thead>
              <tbody>
                {products.map((productItem, index) => (
                  <tr key={productItem.product_id || index}>
                    <td>
                      <img
                        src={productItem.image || 'https://placehold.co/60x60/E9ECEF/333?text=Img'}
                        alt={productItem.name}
                        className="product-image-order"
                        onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/60x60/E9ECEF/333?text=Error"; }}
                      />
                    </td>
                    <td>{productItem.name || "N/A"}</td>
                    <td className="text-center">
                      £{parseFloat(productItem.price || 0).toFixed(2)}
                    </td>
                    <td className="text-center">{productItem.quantity}</td>
                    <td>
                      {productItem.selectedAttributes &&
                        Object.keys(productItem.selectedAttributes).length > 0 ? (
                        <ul className="list-unstyled mb-0 attributes-list">
                          {Object.entries(productItem.selectedAttributes).map(
                            ([key, value]) =>
                              typeof value !== "object" && value && (
                                <li key={key}>
                                  <small><strong>{key.charAt(0).toUpperCase() + key.slice(1)}:</strong> {String(value)}</small>
                                </li>
                              )
                          )}
                        </ul>
                      ) : (
                        <span className="text-muted small">N/A</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderDetails;